// utils/exerciseUtils.js

// Import your enum arrays

// Create lookup maps from your existing enums
import {resistanceTypes} from "../enums/resistanceTypes";
import {targetMuscles} from "../enums/TargetMuscles";
import {SecondaryMuscles} from "../enums/SecondaryMuscles";
import {DEFAULT_MANIPULATION} from "../enums/ManipulationTypes";

const resistanceTypesMap = new Map<string, string>(resistanceTypes.map(item => [item.value, item.name]));
const targetMusclesMap = new Map<string, string>(targetMuscles.map(item => [item.value, item.name]));
const secondaryMusclesMap = new Map<string, string>(SecondaryMuscles.map(item => [item.value, item.name]));

/** MUI color token returned for a resistance type. */
type MuiColorToken =
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'default';

/** Loose per-set rep range used by enriched exercises. */
interface SetRange {
    setIndex?: number;
    minReps: number | null | undefined;
    maxReps: number | null | undefined;
}

/** Loose per-set duration range used by catalyst exercises. */
interface DurationRange {
    minDuration?: number | null;
    maxDuration?: number | null;
}

/** An interval as stored on a template exercise (rep- or duration-based). */
interface TemplateInterval {
    index?: number;
    minReps?: number | null;
    maxReps?: number | null;
    recommendedReps?: number;
    minDuration?: number;
    maxDuration?: number;
    recommendedDuration?: number | null;
    typeSet?: string;
}

/**
 * An exercise definition as returned by the library, plus the loose fields the
 * transformers below read. These are intentionally optional/permissive because
 * this module bridges several server shapes (library definition, template,
 * enriched) and applies runtime fallbacks.
 */
export interface ExerciseLike {
    id?: number | null;
    templateExerciseId?: number | null;
    exerciseId?: number | null;
    index?: number;
    name?: string | null;
    nameHeb?: string | null;
    exerciseName?: string | null;
    targetMuscle?: string | null;
    secondaryMuscle?: string | null;
    resistanceType?: string | null;
    catalyst?: boolean | null;
    imageUrl?: string | null;
    coverUrl?: string | null;
    tempo?: string | null;
    comment?: string | null;
    manipulation?: string | null;
    durationTime?: number | null;
    restTimeInSec?: number | null;
    recommendedReps?: number | null;
    typeSet?: string | null;
    numberOfSets?: number | null;
    superSetGroupId?: string | null;
    intervals?: TemplateInterval[];
    setRanges?: SetRange[];
    durationRanges?: DurationRange[];
    defaultMinDuration?: number | null;
    defaultMaxDuration?: number | null;
    /** Catalyst: the trainer's recommended time per set (seconds); null when left blank. */
    recommendedDuration?: number | null;
    [key: string]: unknown;
}

/** A workout day as consumed by {@link duplicateWorkoutDay}. */
export interface WorkoutLike {
    name?: string | null;
    nameHeb?: string | null;
    exercises?: ExerciseLike[];
}

/** A renderable unit produced by {@link groupExercisesForRendering}. */
export type ExerciseGroup =
    | { type: 'superset'; exerciseA: ExerciseLike; exerciseB: ExerciseLike; startIndex: number }
    | { type: 'single'; exercise: ExerciseLike; startIndex: number };

// Color mapping for resistance types
export const getResistanceTypeColor = (resistanceType: string | null | undefined): MuiColorToken => {
    switch(resistanceType) {
        // Free weights - primary blue
        case 'BARBBELL':
        case 'DUMBBELL':
            return 'primary';

        // Machines - secondary purple/grey
        case 'MACHINE_PLATE_LOADED':
        case 'MACHINE_SELECTORIZED':
        case 'SMITH_MACHINE':
        case 'ASSISTED':
            return 'secondary';

        // Cables - info/cyan
        case 'CABLE_CROSS':
            return 'info';

        // Body weight - success green
        case 'BODY_WEIGHT':
            return 'success';

        // Bands - warning orange
        case 'BAND':
            return 'warning';

        // Cardio equipment - error red
        case 'TREADMILL':
        case 'RUNNING':
        case 'BICYCLE_MACHINE':
        case 'ELLIPTICAL_MACHINE':
        case 'STEP_MACHINE':
        case 'WALKING':
            return 'error';

        default:
            return 'default';
    }
};

// Format enum values using your existing translations
export const formatEnumValue = (enumValue: string | null | undefined): string => {
    if (!enumValue) return '';

    // Try to find translation in your enum arrays
    return resistanceTypesMap.get(enumValue) ||
        targetMusclesMap.get(enumValue) ||
        secondaryMusclesMap.get(enumValue) ||
        enumValue; // Fallback to original value if not found
};

// Get muscle group category for styling
export const getMuscleGroupCategory = (targetMuscle: string | null | undefined): string => {
    if (!targetMuscle) return 'default';

    if (targetMuscle.startsWith('LEGS_') || targetMuscle === 'FEET') return 'legs';
    if (targetMuscle.startsWith('CHEST_')) return 'chest';
    if (targetMuscle.startsWith('BACK_')) return 'back';
    // The neck sits with the traps, so it takes the shoulders' colour.
    if (targetMuscle.startsWith('SHOULDERS_') || targetMuscle === 'NECK') return 'shoulders';
    if (targetMuscle.startsWith('ARMS_')) return 'arms';
    if (targetMuscle.startsWith('ABS')) return 'abs';
    if (targetMuscle === 'CARDIO') return 'cardio';

    return 'default';
};

// Get muscle group color and styling
export const getMuscleGroupColor = (targetMuscle: string | null | undefined): string => {
    const category = getMuscleGroupCategory(targetMuscle);

    switch(category) {
        case 'legs':
            return '#4CAF50'
        case 'chest':
            return '#2196F3'; // Blue
        case 'back':
            return '#FF9800';
        case 'shoulders':
            return '#9C27B0';
        case 'arms':
            return '#F44336'; // Indigo
        case 'abs':
            return '#FF5722';
        case 'cardio':
            return '#E91E63';
        default:
            return '#455A64';
    }
};

// Get equipment category for grouping
export const getEquipmentCategory = (resistanceType: string | null | undefined): string => {
    if (!resistanceType) return 'other';

    const cardioTypes = ['TREADMILL', 'RUNNING', 'BICYCLE_MACHINE', 'ELLIPTICAL_MACHINE', 'STEP_MACHINE', 'WALKING'];
    const machineTypes = ['MACHINE_PLATE_LOADED', 'MACHINE_SELECTORIZED', 'SMITH_MACHINE', 'ASSISTED'];
    const freeWeightTypes = ['BARBBELL', 'DUMBBELL'];

    if (cardioTypes.includes(resistanceType)) return 'cardio';
    if (machineTypes.includes(resistanceType)) return 'machine';
    if (freeWeightTypes.includes(resistanceType)) return 'freeweight';
    if (resistanceType === 'CABLE_CROSS') return 'cable';
    if (resistanceType === 'BODY_WEIGHT') return 'bodyweight';
    if (resistanceType === 'BAND') return 'band';

    return 'other';
};

// Helper to check if exercise is catalyst (timed sets with weight)
export const isCatalystExercise = (exercise: ExerciseLike): boolean => {
    return exercise.catalyst === true;
};

// Helper to check if exercise is cardio
export const isCardioExercise = (exercise: ExerciseLike): boolean => {
    const cardioTypes = ['TREADMILL', 'RUNNING', 'BICYCLE_MACHINE', 'ELLIPTICAL_MACHINE', 'STEP_MACHINE', 'WALKING'];
    return exercise.targetMuscle === 'CARDIO' || cardioTypes.includes(exercise.resistanceType ?? '');
};

/**
 * Transforms an ExerciseDefinition from the library into a TemplateExercise
 * @param {Object} exerciseDefinition - The exercise from the library
 * @param {number} index - The index position in the workout
 * @returns {Object} TemplateExercise object
 */
export const createTemplateExerciseFromDefinition = (exerciseDefinition: ExerciseLike, index = 0): ExerciseLike => {
    const isCardio = isCardioExercise(exerciseDefinition);
    const isCatalyst = isCatalystExercise(exerciseDefinition);
    return {
        id: null, // Will be set by backend when saved
        createdDate: null, // Will be set by backend
        index: index,
        targetMuscle: exerciseDefinition.targetMuscle || 'UNKNOWN',
        catalyst: exerciseDefinition.catalyst || false,
        exerciseId: exerciseDefinition.id,
        exerciseName: exerciseDefinition.nameHeb || 'Unknown Exercise',
        secondaryMuscle: exerciseDefinition.secondaryMuscle || 'Unknown Exercise',
        imageUrl: exerciseDefinition.imageUrl || null,
        coverUrl: exerciseDefinition.coverUrl || null,
        tempo: (isCardio || isCatalyst) ? null : "T1_0_1_0",
        comment: null,
        manipulation: isCardio ? null : DEFAULT_MANIPULATION,
        durationTime: isCardio ? 60 : null,
        restTimeInSec: isCardio ? 0 : 120,
        recommendedReps: (isCardio || isCatalyst) ? 0 : 10,
        typeSet: "REGULAR_SET",
        numberOfSets: isCardio ? 0 : 3,
        superSetGroupId: null,
        // Catalyst defaults: 3 sets with 45s duration
        ...(isCatalyst && {
            defaultMinDuration: 30,
            defaultMaxDuration: 60,
            recommendedDuration: 45,
        }),
    };
};

/**
 * Enriches a TemplateExercise with full ExerciseDefinition data
 * @param {Object} templateExercise - The template exercise
 * @param {Object} exerciseDefinition - The full exercise definition
 * @returns {Object} Enriched template exercise
 */
export const enrichTemplateExercise = (templateExercise: ExerciseLike, exerciseDefinition: ExerciseLike): ExerciseLike => {
    // Convert intervals to setRanges format (if they have custom per-set ranges)
    let setRanges: SetRange[] | undefined = undefined;
    if (templateExercise.intervals && templateExercise.intervals.length > 0) {
        // Check if any interval has custom minReps/maxReps
        const hasCustomRanges = templateExercise.intervals.some(
            interval => interval.minReps !== null || interval.maxReps !== null
        );

        if (hasCustomRanges) {
            setRanges = templateExercise.intervals.map((interval, idx) => ({
                setIndex: idx,
                minReps: interval.minReps,
                maxReps: interval.maxReps
            }));
        }
    }

    return {
        templateExerciseId: templateExercise.id,
        index: templateExercise.index,
        exerciseId: templateExercise.exerciseId,
        exerciseName: exerciseDefinition.nameHeb || exerciseDefinition.exerciseName || exerciseDefinition.name,
        targetMuscle: exerciseDefinition.targetMuscle || templateExercise.targetMuscle,
        catalyst: exerciseDefinition.catalyst || templateExercise.catalyst || false,
        secondaryMuscle: exerciseDefinition.secondaryMuscle,
        tempo: templateExercise.tempo,
        comment: templateExercise.comment,
        manipulation: templateExercise.manipulation,
        durationTime: templateExercise.durationTime,
        restTimeInSec: templateExercise.restTimeInSec,
        recommendedReps: templateExercise.recommendedReps,
        typeSet: templateExercise.typeSet,
        numberOfSets: templateExercise.numberOfSets,
        superSetGroupId: templateExercise.superSetGroupId || null,
        setRanges: setRanges
    };
};

/**
 * Cleans an enriched template exercise back to template format for saving
 * @param {Object} enrichedExercise - The enriched exercise
 * @param {number} index - The index position
 * @returns {Object} Clean template exercise for API
 */
export const cleanTemplateExerciseForSaving = (enrichedExercise: ExerciseLike, index: number): ExerciseLike => {
    // Cardio exercises: no intervals, just duration
    if (enrichedExercise.targetMuscle === 'CARDIO') {
        return {
            id: enrichedExercise.templateExerciseId || null,
            exerciseId: enrichedExercise.exerciseId || enrichedExercise.id,
            index: index,
            tempo: null,
            comment: enrichedExercise.comment || null,
            manipulation: null,
            durationTime: enrichedExercise.durationTime || 60,
            restTimeInSec: 0,
            recommendedReps: 0,
            typeSet: "REGULAR_SET",
            numberOfSets: 0,
            superSetGroupId: null,
            intervals: []
        };
    }

    // Catalyst exercises: intervals with duration ranges instead of rep ranges
    if (enrichedExercise.catalyst) {
        const intervals: TemplateInterval[] = [];
        const numberOfSets = enrichedExercise.numberOfSets || 3;
        const durationRanges = enrichedExercise.durationRanges || [];

        for (let i = 0; i < numberOfSets; i++) {
            const range = durationRanges[i];
            const minDuration = range?.minDuration || enrichedExercise.defaultMinDuration || 30;
            const maxDuration = range?.maxDuration || enrichedExercise.defaultMaxDuration || 60;
            intervals.push({
                index: i,
                minDuration,
                maxDuration,
                // The trainer's own value, or null when left blank (the app then targets maxDuration).
                recommendedDuration: enrichedExercise.recommendedDuration ?? null,
                typeSet: "REGULAR_SET"
            });
        }

        return {
            id: enrichedExercise.templateExerciseId || null,
            exerciseId: enrichedExercise.exerciseId || enrichedExercise.id,
            index: index,
            tempo: null,
            comment: enrichedExercise.comment || null,
            manipulation: enrichedExercise.manipulation || DEFAULT_MANIPULATION,
            durationTime: null,
            restTimeInSec: enrichedExercise.restTimeInSec || 120,
            recommendedReps: 0,
            typeSet: "REGULAR_SET",
            numberOfSets: numberOfSets,
            superSetGroupId: enrichedExercise.superSetGroupId || null,
            intervals: intervals
        };
    }

    // Convert setRanges to intervals format
    const intervals: TemplateInterval[] = [];
    const numberOfSets = enrichedExercise.numberOfSets || 3;
    const setRanges = enrichedExercise.setRanges || [];

    for (let i = 0; i < numberOfSets; i++) {
        const setRange = setRanges[i];
        const minReps = setRange?.minReps || null;
        const maxReps = setRange?.maxReps || null;
        const recReps = (minReps != null && maxReps != null) ? Math.round((minReps + maxReps) / 2) : (enrichedExercise.recommendedReps || 10);
        intervals.push({
            index: i,
            minReps,
            maxReps,
            recommendedReps: recReps,
            typeSet: "REGULAR_SET"
        });
    }

    return {
        id: enrichedExercise.templateExerciseId || null,
        exerciseId: enrichedExercise.exerciseId || enrichedExercise.id,
        // exerciseName: enrichedExercise.exerciseName || enrichedExercise.nameHeb,
        // secondaryMuscle: enrichedExercise.secondaryMuscle || enrichedExercise.secondaryMuscle,
        index: index,
        tempo: enrichedExercise.tempo || "T1_0_1_0",
        comment: enrichedExercise.comment || null,
        manipulation: enrichedExercise.manipulation || DEFAULT_MANIPULATION,
        durationTime: enrichedExercise.durationTime || null,
        restTimeInSec: enrichedExercise.restTimeInSec || 120,
        recommendedReps: enrichedExercise.recommendedReps || 10,
        typeSet: enrichedExercise.typeSet || "REGULAR_SET",
        numberOfSets: enrichedExercise.numberOfSets || 3,
        superSetGroupId: enrichedExercise.superSetGroupId || null,
        intervals: intervals
    };
};

/**
 * Helper to get the next index for a new exercise in a workout
 * @param {Array} exercises - Current exercises in the workout
 * @returns {number} Next available index
 */
export const getNextExerciseIndex = (exercises: ExerciseLike[] = []): number => {
    if (exercises.length === 0) return 0;
    return Math.max(...exercises.map(ex => ex.index || 0)) + 1;
};

/**
 * Links two exercises as a super set by assigning them a shared UUID
 * @param {Object} exerciseA - First exercise
 * @param {Object} exerciseB - Second exercise
 * @returns {{ exerciseA: Object, exerciseB: Object }} Updated exercises with shared superSetGroupId
 */
export const createSuperSetLink = (exerciseA: ExerciseLike, exerciseB: ExerciseLike): { exerciseA: ExerciseLike; exerciseB: ExerciseLike } => {
    const groupId = crypto.randomUUID();
    return {
        exerciseA: { ...exerciseA, superSetGroupId: groupId },
        exerciseB: { ...exerciseB, superSetGroupId: groupId, numberOfSets: exerciseA.numberOfSets }
    };
};

/**
 * Removes super set link from an exercise
 * @param {Object} exercise - The exercise to unlink
 * @returns {Object} Exercise with superSetGroupId cleared
 */
export const removeSuperSetLink = (exercise: ExerciseLike): ExerciseLike => {
    return { ...exercise, superSetGroupId: null };
};

/**
 * Checks if two exercises are linked as a super set
 */
export const areSuperSetPartners = (exerciseA: ExerciseLike | null | undefined, exerciseB: ExerciseLike | null | undefined): boolean => {
    return exerciseA?.superSetGroupId != null &&
        exerciseA.superSetGroupId === exerciseB?.superSetGroupId;
};

/**
 * Superset accent colors (violet palette - distinct from primary teal and secondary grey)
 */
export const SUPERSET_COLORS: Record<string, string> = {
    accent: '#7C3AED',
    accentLight: '#8B5CF6',
    background: '#F5F3FF',
    backgroundHover: '#EDE9FE',
    border: '#DDD6FE',
    text: '#5B21B6',
    badgeA: '#7C3AED',
    badgeB: '#A78BFA',
};

/**
 * Groups exercises into renderable units: standalone exercises or superset pairs.
 * Returns an array of { type: 'single' | 'superset', exercise?, exerciseA?, exerciseB?, startIndex }
 */
export const groupExercisesForRendering = (dayExercises: ExerciseLike[] = []): ExerciseGroup[] => {
    const groups: ExerciseGroup[] = [];
    let i = 0;

    while (i < dayExercises.length) {
        const current = dayExercises[i];
        const next = dayExercises[i + 1];

        if (next && areSuperSetPartners(current, next)) {
            groups.push({
                type: 'superset',
                exerciseA: current,
                exerciseB: next,
                startIndex: i,
            });
            i += 2;
        } else {
            groups.push({
                type: 'single',
                exercise: current,
                startIndex: i,
            });
            i += 1;
        }
    }

    return groups;
};

/**
 * Reorder a day's exercises by moving the GROUP that starts at `fromStartIndex` to the
 * position of the group that starts at `toStartIndex`. A "group" is a standalone exercise
 * (span 1) or a superset pair (span 2), exactly as {@link groupExercisesForRendering} sees it.
 *
 * <p>Why this exists: the drag UI makes a superset a single draggable unit whose drag index is
 * the pair's FIRST exercise (its group startIndex). A naive `splice(from, 1)` moved only that
 * first exercise and orphaned its partner, silently splitting the superset. Operating on whole
 * GROUPS fixes that and also normalizes the drop point to a group boundary — you can never land
 * between a pair's A and B, so a different superset can't be split either.
 *
 * <p>Both drag handlers pass group start indices (from `groupExercisesForRendering`), so this
 * works for singles and supersets, in both drag directions. Returns a NEW array with each
 * exercise's `index` re-sequenced 0..n-1; superset partners stay adjacent. If either index does
 * not begin a group (or they resolve to the same group), the original array is returned unchanged.
 *
 * @param {Object[]} exercises - the day's exercises, in order (each may carry superSetGroupId)
 * @param {number} fromStartIndex - the group start index being dragged
 * @param {number} toStartIndex - the group start index to drop at
 * @returns {Object[]} a new, re-sequenced exercises array (partners kept together)
 */
export const reorderExerciseGroups = (exercises: ExerciseLike[] = [], fromStartIndex: number, toStartIndex: number): ExerciseLike[] => {
    if (!Array.isArray(exercises) || exercises.length === 0) return exercises;

    const groups = groupExercisesForRendering(exercises);
    const fromGroup = groups.findIndex(g => g.startIndex === fromStartIndex);
    const toGroup = groups.findIndex(g => g.startIndex === toStartIndex);

    // Either endpoint isn't a real group boundary, or it's a no-op move.
    if (fromGroup === -1 || toGroup === -1 || fromGroup === toGroup) return exercises;

    // Move whole groups, then flatten back to a flat exercise list.
    const reorderedGroups = [...groups];
    const [movedGroup] = reorderedGroups.splice(fromGroup, 1);
    reorderedGroups.splice(toGroup, 0, movedGroup);

    const flattened: ExerciseLike[] = [];
    reorderedGroups.forEach(group => {
        if (group.type === 'superset') {
            flattened.push(group.exerciseA, group.exerciseB);
        } else {
            flattened.push(group.exercise);
        }
    });

    // Re-sequence index on shallow copies so partners remain adjacent and index reflects order.
    return flattened.map((exercise, index) => ({ ...exercise, index }));
};

/**
 * Duplicates a workout day with fresh IDs and superset UUIDs.
 * @param {Object} workout - The workout to duplicate
 * @param {number} newIndex - The index for the new workout
 * @returns {Object} A new workout object
 */
export const duplicateWorkoutDay = (workout: WorkoutLike, newIndex: number): { id: null; index: number; name: string; nameHeb: string; exercises: ExerciseLike[] } => {
    // Map old superSetGroupIds to new ones
    const superSetIdMap = new Map<string, string>();

    const duplicatedExercises = (workout.exercises || []).map((exercise, idx) => {
        let newSuperSetGroupId: string | null = null;

        if (exercise.superSetGroupId) {
            if (!superSetIdMap.has(exercise.superSetGroupId)) {
                superSetIdMap.set(exercise.superSetGroupId, crypto.randomUUID());
            }
            newSuperSetGroupId = superSetIdMap.get(exercise.superSetGroupId)!;
        }

        return {
            ...exercise,
            id: null,
            templateExerciseId: null,
            index: idx,
            superSetGroupId: newSuperSetGroupId,
        };
    });

    return {
        id: null,
        index: newIndex,
        name: workout.name ? `${workout.name} (Copy)` : `Workout ${newIndex + 1}`,
        nameHeb: workout.nameHeb ? `${workout.nameHeb} (העתק)` : `אימון ${newIndex + 1}`,
        exercises: duplicatedExercises,
    };
};

const exerciseUtils = {
    getResistanceTypeColor,
    formatEnumValue,
    getMuscleGroupCategory,
    getMuscleGroupColor,
    getEquipmentCategory,
    isCardioExercise,
    isCatalystExercise
};

export default exerciseUtils;